<template>
  <div class="site-cards">
    <!-- Toolbar -->
    <table-toolbar
      v-if="showToolbar"
      :show-title="showTitle"
      :title="title"
      :show-count="true"
      :current-count="filteredSites.length"
      :total-count="totalSites"
      :searchable="showSearch"
      :search-query="searchQuery"
      @search-change="searchQuery = $event"
      :show-per-page-selector="showDisplayCount"
      :per-page="displayCount"
      @per-page-change="displayCount = $event"
      :show-refresh="false"
      :show-column-visibility="viewMode === 'list' && showColumnVisibility"
      :columns="availableTableColumns"
      :visible-columns="visibleColumns"
      @column-toggle="handleColumnVisibilityChange"
      :show-view-toggle="showViewToggle"
      :view-mode="viewMode"
      @view-mode-change="viewMode = $event"
      :show-sort-filter="showSortFilter"
      :sort-options="sortOptions"
      :sort-by="sortBy"
      :sort-direction="sortDirection"
      @sort-change="sortBy = $event"
      @sort-direction-change="sortDirection = $event"
      :filter-options="filterOptionsConfig"
      @filter-change="handleFilterChange"
      @clear-filters="clearFilters"
      :disabled="loading"
    >
      <!-- Action Buttons Slot -->
      <template #action-buttons>
        <!-- New Site Button -->
        <themed-button 
          v-if="showNewSiteButton && $gate.can('create', 'site')"
          @click="createSite"
          variant="default"
          size="default"
          aria-label="Create new site"
        >
          <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
          <span>New Site</span>
        </themed-button>
      </template>
    </table-toolbar>
    
    <!-- Grid View -->
    <themed-grid
      v-if="viewMode === 'grid'"
      :data="filteredSites"
      :fields="gridFields"
      :columns="3"
      :pagination="paginationConfig"
      :clickable="true"
      :loading="loading"
      empty-message="No sites found"
      empty-icon="site-icon"
      item-key="id"
      @card-click="viewSite"
      @page-change="handleTablePageChange"
    />
    
    <!-- List View -->
    <themed-table
      v-else
      ref="sitesTable"
      :data="filteredSites"
      :columns="visibleTableColumns"
      :pagination="paginationConfig"
      :hoverable="true"
      :searchable="false"
      :column-visibility="false"
      empty-message="No sites found"
      empty-icon="site-icon"
      aria-label="Sites Table"
      @action="handleAction"
      @row-click="handleRowClick"
      @page-change="handleTablePageChange"
    />
  </div>
</template>

<script>
import ThemedTable from '../ThemedTable.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TableToolbar from '../table/TableToolbar.vue';
import { formatNumber, formatShorthandCurrency, formatDate } from '../../../../utils/formatters';

export default {
  name: 'SiteCards',
  
  components: {
    ThemedTable,
    ThemedGrid,
    ThemedButton,
    ThemeIcon,
    TableToolbar
  },
  
  props: {
    siteData: {
      type: Array,
      default: () => []
    },
    
    // Toolbar configuration
    showToolbar: {
      type: Boolean,
      default: true
    },
    
    showSearch: {
      type: Boolean,
      default: true
    },
    
    showViewToggle: {
      type: Boolean,
      default: true
    },
    
    showSortFilter: {
      type: Boolean,
      default: true
    },
    
    showDisplayCount: {
      type: Boolean,
      default: true
    },
    
    showNewSiteButton: {
      type: Boolean,
      default: true
    },
    
    showTitle: {
      type: Boolean,
      default: true
    },
    
    showArchivedToggle: {
      type: Boolean,
      default: true
    },
    
    // View configuration
    defaultViewMode: {
      type: String,
      default: 'grid', // 'grid' or 'list'
      validator: (value) => ['grid', 'list'].includes(value)
    },
    
    // Pagination configuration
    defaultPageSize: {
      type: Number,
      default: 10,
      validator: (value) => [5, 10, 20, 50, 100].includes(value)
    },
    
    showPagination: {
      type: Boolean,
      default: true
    },
    
    // Filter configuration
    defaultShowArchived: {
      type: Boolean,
      default: false
    },

    // Context flags
    fromEstimatedWaste: {
      type: Boolean,
      default: false
    },
    
    // Sort configuration
    defaultSortBy: {
      type: String,
      default: '',
      validator: (value) => [
        '', 'address', 'size', 'base_psf', 'current_psf', 'remaining_lease_cost',
        'estimated_waste', 'time', 'utilization', 'lease_end_date', 'next_key_date',
        'waste' // legacy alias for remaining_lease_cost
      ].includes(value)
    },
    
    defaultSortDirection: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },
    
    // Card configuration (passed to SiteCard)
    cardConfig: {
      type: Object,
      default: () => ({})
    },
    
    // Custom title
    title: {
      type: String,
      default: 'Sites'
    },
    
    // Disable localStorage persistence
    disablePreferences: {
      type: Boolean,
      default: false
    },
    
    // Custom columns for table view
    columns: {
      type: Array,
      default: null
    },
    
    // Show column visibility toggle
    showColumnVisibility: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      sites: [],
      viewMode: this.defaultViewMode,
      searchQuery: '',
      sortBy: this.defaultSortBy,
      sortDirection: this.defaultSortDirection,
      showArchivedSites: this.defaultShowArchived,
      displayCount: this.defaultPageSize,
      currentPage: 1,
      sortFilterOpen: false,
      loading: false,
      visibleColumns: [],
      // Filter values
      leaseEndDateFilter: '',
      nextKeyDateFilter: '',
      customLeaseStartDate: null,
      customLeaseEndDate: null,
      customKeyStartDate: null,
      customKeyEndDate: null,
      stateFilter: '',
      leaseOrOwnFilter: '',
      utilizationFilter: '',
      sizeFilter: '',
      utilizationBandOptions: [
        { label: 'Any Utilization', value: '' },
        { label: 'Over 100%', value: 'over' },
        { label: '90% and above', value: '90' },
        { label: '70% to 89%', value: '70' },
        { label: 'Below 70%', value: 'under70' }
      ],
      sizeBandOptions: [
        { label: 'Any Size', value: '' },
        { label: 'Under 25,000 sq ft', value: '0-25000' },
        { label: '25,000 - 100,000 sq ft', value: '25000-100000' },
        { label: '100,000 - 250,000 sq ft', value: '100000-250000' },
        { label: '250,000 sq ft and above', value: '250000-' }
      ],
      leaseEndDateOptions: [
        { label: 'Any Time', value: '' },
        { label: 'Next 30 Days', value: '30' },
        { label: 'Next 60 Days', value: '60' },
        { label: 'Next 90 Days', value: '90' },
        { label: 'Next 6 Months', value: '180' },
        { label: 'Next Year', value: '365' },
        { label: 'Expired', value: 'expired' },
        { label: 'Custom Range', value: 'custom' }
      ],
      nextKeyDateOptions: [
        { label: 'Any Time', value: '' },
        { label: 'Next 7 Days', value: '7' },
        { label: 'Next 14 Days', value: '14' },
        { label: 'Next 30 Days', value: '30' },
        { label: 'Next 60 Days', value: '60' },
        { label: 'Next 90 Days', value: '90' },
        { label: 'Overdue', value: 'overdue' },
        { label: 'Custom Range', value: 'custom' }
      ]
    };
  },
  
  computed: {
    // The previous "Waste" entry sorted on remaining_lease_cost, so it was both
    // mislabelled and the only financial metric available. Remaining Cost is now
    // named for what it does, the other financial metrics are selectable, and a
    // genuine Est. Waste sort appears only where that field is loaded.
    sortOptions() {
      const options = [
        { label: 'Address', value: 'address' },
        { label: 'Square Feet', value: 'size' },
        { label: 'Base PSF', value: 'base_psf' },
        { label: 'Current PSF', value: 'current_psf' },
        { label: 'Remaining Cost', value: 'remaining_lease_cost' },
        { label: 'Time Remaining', value: 'time' },
        { label: 'Utilization', value: 'utilization' },
        { label: 'Lease End Date', value: 'lease_end_date' },
        { label: 'Next Key Date', value: 'next_key_date' }
      ];

      if (this.fromEstimatedWaste) {
        options.splice(5, 0, { label: 'Est. Waste', value: 'estimated_waste' });
      }

      return options;
    },

    // Built from the sites actually loaded, so it never offers a state with no
    // matching rows.
    stateOptions() {
      const states = [...new Set(
        this.sites
          .map(site => site.building?.state || site.state)
          .filter(Boolean)
      )].sort();

      return [
        { label: 'Any State', value: '' },
        ...states.map(state => ({ label: state, value: state }))
      ];
    },

    leaseOrOwnOptions() {
      const values = [...new Set(
        this.sites.map(site => site.lease_or_own).filter(Boolean)
      )].sort();

      return [
        { label: 'Leased & Owned', value: '' },
        ...values.map(value => ({ label: value, value }))
      ];
    },

    filterOptionsConfig() {
      const filters = [];
      
      if (this.showArchivedToggle) {
        filters.push({
          type: 'toggle',
          label: 'Show Archived Sites',
          value: this.showArchivedSites,
          model: 'showArchivedSites'
        });
      }
      
      // Lease End Date Filter
      filters.push({
        type: 'select',
        label: 'Lease End Date',
        value: this.leaseEndDateFilter,
        options: this.leaseEndDateOptions,
        model: 'leaseEndDateFilter'
      });
      
      // Custom Lease Date Range (shown when 'custom' is selected)
      if (this.leaseEndDateFilter === 'custom') {
        filters.push({
          type: 'date-range',
          label: 'Custom Lease Date Range',
          startValue: this.customLeaseStartDate,
          endValue: this.customLeaseEndDate,
          startModel: 'customLeaseStartDate',
          endModel: 'customLeaseEndDate',
          startPlaceholder: 'Start Date',
          endPlaceholder: 'End Date'
        });
      }
      
      // Next Key Date Filter
      filters.push({
        type: 'select',
        label: 'Next Key Date',
        value: this.nextKeyDateFilter,
        options: this.nextKeyDateOptions,
        model: 'nextKeyDateFilter'
      });

      // Portfolio slicing. A director with a few hundred sites could previously
      // only filter on archived status and two date ranges.
      if (this.stateOptions.length > 2) {
        filters.push({
          type: 'select',
          label: 'State',
          value: this.stateFilter,
          options: this.stateOptions,
          model: 'stateFilter'
        });
      }

      if (this.leaseOrOwnOptions.length > 2) {
        filters.push({
          type: 'select',
          label: 'Lease or Own',
          value: this.leaseOrOwnFilter,
          options: this.leaseOrOwnOptions,
          model: 'leaseOrOwnFilter'
        });
      }

      filters.push({
        type: 'select',
        label: 'Utilization',
        value: this.utilizationFilter,
        options: this.utilizationBandOptions,
        model: 'utilizationFilter'
      });

      filters.push({
        type: 'select',
        label: 'Square Feet',
        value: this.sizeFilter,
        options: this.sizeBandOptions,
        model: 'sizeFilter'
      });
      
      // Custom Key Date Range (shown when 'custom' is selected)
      if (this.nextKeyDateFilter === 'custom') {
        filters.push({
          type: 'date-range',
          label: 'Custom Key Date Range',
          startValue: this.customKeyStartDate,
          endValue: this.customKeyEndDate,
          startModel: 'customKeyStartDate',
          endModel: 'customKeyEndDate',
          startPlaceholder: 'Start Date',
          endPlaceholder: 'End Date'
        });
      }
      
      return filters;
    },
    
    gridFields() {
      // Configure fields for ThemedGrid
      const fields = [
        // Image
        {
          field: 'image_url',
          type: 'image',
          defaultImage: '/img/placeholder-building.jpg'
        },
        // Title
        {
          field: 'building.address_line_1',
          type: 'title',
          transform: (value) => value || 'Unknown Site'
        },
        // Subtitle (location)
        {
          field: 'building',
          type: 'subtitle',
          transform: (value) => {
            const city = value?.city || '';
            const state = value?.state || '';
            return `${city}${city && state ? ', ' : ''}${state}`;
          }
        },
        // Archived badge (top-left)
        {
          field: 'archived_at',
          type: 'badge',
          position: 'topLeft',
          transform: (value) => value ? 'ARCHIVED' : null,
          class: 'badge--orange'
        },
        // Danger Zone badge (top-left)
        {
          field: 'building.is_in_danger_zone',
          type: 'badge',
          position: 'topLeft',
          transform: (value) => value ? 'DANGER ZONE' : null,
          class: 'badge--red'
        },
        // Due In badge (top-right)
        {
          field: 'due_in_text',
          type: 'badge',
          position: 'topRight',
          transform: (value, item) => {
            if (item.lease_or_own === 'Owned') return 'OWNED';
            if (!item.leases || item.leases.length === 0) return 'NO LEASE';
            if (item.due_in_days === 0) return 'EXPIRED';
            if (value) return `DUE IN ${value.toUpperCase()}`;
            return 'NO LEASE INFORMATION';
          },
          classFunction: (item) => this.getDueInBadgeClass(item)
        },
        // Base PSF and Current PSF (two columns)
        {
          columns: 2,
          fields: [
            {
              field: 'base_psf',
              label: 'BASE PSF',
              type: 'currency',
              precision: 2,
              transform: (value) => value / 100
            },
            {
              field: 'current_psf',
              label: 'CURRENT PSF',
              type: 'currency',
              precision: 2,
              transform: (value) => value / 100
            }
          ]
        },
        // Square Feet
        {
          field: 'size',
          label: 'SQUARE FEET',
          type: 'number',
          transform: (value, item) => value || item.square_footage
        },
        // Remaining Cost
        {
          field: 'remaining_lease_cost',
          label: 'REMAINING',
          type: 'shorthand-currency',
          transform: (value, item) => {
            if (item.shortened_remaining_lease_cost) {
              return item.shortened_remaining_lease_cost;
            }
            return value;
          },
          classFunction: (item) => {
            const remaining = item.remaining_lease_cost || 0;
            return remaining === 0 || remaining === '0' ? 'themed-grid__field-value--zero' : '';
          }
        }
      ];

      // Add Estimated Waste fields if needed
      if (this.fromEstimatedWaste) {
        fields.push(
          // Lease dates (two columns)
          {
            columns: 2,
            fields: [
              {
                field: 'lease_start_date',
                label: 'LEASE START',
                type: 'date'
              },
              {
                field: 'lease_end_date',
                label: 'LEASE END',
                type: 'date'
              }
            ]
          },
          // NPV Discount and NPV (two columns)
          {
            columns: 2,
            fields: [
              {
                field: 'npv_discount_rate',
                label: 'NPV DISCOUNT',
                type: 'percentage',
                precision: 0
              },
              {
                field: 'npv',
                label: 'NPV',
                type: 'shorthand-currency',
                transform: (value, item) => {
                  if (item.shortened_npv) return item.shortened_npv;
                  return value / 100;
                },
                class: 'themed-grid__field-value--success'
              }
            ]
          },
          // Estimated Waste
          {
            field: 'estimated_waste',
            label: 'EST WASTE',
            type: 'shorthand-currency',
            transform: (value, item) => {
              if (item.shortened_estimated_waste) return item.shortened_estimated_waste;
              return value;
            },
            class: 'themed-grid__field-value--danger'
          }
        );
      } else {
        // Add Lease End Date for regular sites page
        fields.push({
          field: 'lease_end_date',
          label: 'LEASE END DATE',
          type: 'date',
          visible: (item) => !!item.lease_end_date
        });
      }

      // Add Utilization as a footer badge
      fields.push({
        field: 'utilization',
        label: 'UTILIZED',
        type: 'percentage',
        precision: 0,
        labelInValue: true,
        badge: true,
        badgeClass: (value, item) => this.getUtilizationBadgeClass(item),
        class: 'mt-3'
      });

      return fields;
    },
    
    tableColumns() {
      // Use custom columns if provided
      if (this.columns) {
        return this.columns;
      }
      
      // Default columns
      return [
        {
          field: 'image_url',
          label: 'Image',
          type: 'image',
          width: '80px',
          altField: 'building.address_line_1',
          sortable: false,
          showInTable: true,
          alwaysVisible: true
        },
        {
          field: 'building.address_line_1',
          label: 'Address',
          type: 'text',
          sortable: true,
          showInTable: true,
          alwaysVisible: true
        },
        {
          // Shown on the grid card but previously had no table column at all, so
          // switching to list view silently dropped it. Off by default because the
          // table already defaults to nine columns; selectable from the picker.
          field: 'building.city',
          label: 'City',
          type: 'text',
          width: '120px',
          align: 'left',
          sortable: true,
          showInTable: false
        },
        {
          field: 'building.state',
          label: 'State',
          type: 'text',
          width: '60px',
          align: 'center',
          sortable: true,
          showInTable: true
        },
        {
          // Also grid-card only until now. transform matches gridFields, which
          // falls back to square_footage when size is absent.
          field: 'size',
          label: 'Square Feet',
          type: 'number',
          width: '110px',
          align: 'right',
          sortable: true,
          transform: (value, item) => value || item.square_footage || 0,
          showInTable: false
        },
        {
          field: 'building.country',
          label: 'Country',
          type: 'text',
          width: '70px',
          align: 'center',
          sortable: true,
          showInTable: true,
          transform: (value) => value?.country_code_2 || ''
        },
        {
          field: 'lease_or_own',
          label: 'Lease/Own',
          type: 'text',
          width: '120px',
          sortable: true,
          showInTable: true
        },
        {
          field: 'due_in_text',
          label: 'Due In',
          type: 'text',
          badge: true,
          width: '140px',
          align: 'center',
          sortable: true,
          sortField: 'due_in_days',
          showInTable: true,
          badgeClass: (value, row) => this.getDueInBadgeClass(row)
        },
        {
          field: 'base_psf',
          label: 'Base PSF',
          type: 'currency',
          sortable: true,
          width: '100px',
          align: 'right',
          precision: 2,
          showInTable: false,
          transform: (value) => value / 100  // Convert cents to dollars
        },
        {
          field: 'current_psf',
          label: 'Current PSF',
          type: 'currency',
          sortable: true,
          width: '120px',
          align: 'right',
          precision: 2,
          showInTable: false,
          transform: (value) => value / 100  // Convert cents to dollars
        },
        {
          field: 'lease_cost',
          label: 'Lease Cost',
          type: 'currency',
          sortable: true,
          width: '120px',
          align: 'right',
          precision: 0,
          showInTable: false,
          transform: (value) => value / 100  // Convert cents to dollars
        },
        {
          field: 'remaining_lease_cost',
          label: 'Remaining Cost',
          type: 'currency',
          width: '120px',
          align: 'right',
          sortable: true,
          shorthand: true,
          showInTable: true
        },
        {
          field: 'utilization',
          label: 'Utilization',
          type: 'percentage',
          badge: true,
          width: '120px',
          align: 'right',
          precision: 0,
          sortable: true,
          showInTable: true,
          badgeClass: (value, row) => this.getUtilizationBadgeClass(row)
        },
        {
          field: 'lease_start_date',
          label: 'Lease Start',
          type: 'date',
          sortable: true,
          width: '120px',
          align: 'center',
          contextual: true,
          showInTable: this.fromEstimatedWaste
        },
        {
          field: 'lease_end_date',
          label: 'Lease End',
          type: 'date',
          sortable: true,
          width: '120px',
          align: 'center',
          contextual: true,
          showInTable: this.fromEstimatedWaste
        },
        {
          field: 'npv_discount_rate',
          label: 'NPV Discount',
          type: 'percentage',
          sortable: true,
          width: '120px',
          align: 'right',
          precision: 0,
          contextual: true,
          showInTable: this.fromEstimatedWaste
        },
        {
          field: 'npv',
          label: 'NPV',
          type: 'currency',
          sortable: true,
          width: '120px',
          align: 'right',
          shorthand: true,
          contextual: true,
          showInTable: this.fromEstimatedWaste,
          badge: true,
          badgeVariant: 'default',
          transform: (value) => value / 100  // Convert cents to dollars
        },
        {
          field: 'estimated_waste',
          label: 'Est. Waste',
          type: 'currency',
          sortable: true,
          width: '120px',
          align: 'right',
          shorthand: true,
          contextual: true,
          showInTable: this.fromEstimatedWaste,
          badge: true,
          badgeVariant: 'destructive',
          // Already in dollars - getEstimatedWasteAttribute divides npv by 100 before
          // applying the utilization shortfall. Without an explicit transform the shorthand
          // path divides by 100 again and the column reads 100x low.
          transform: (value) => value
        }
      ];
    },
    
    // The set of columns the column picker may offer. This deliberately does NOT
    // filter on showInTable: that flag only decides which columns start switched
    // on (see getDefaultVisibleColumns). Filtering on it here meant every column
    // in the picker was already enabled, so columns could be removed but never
    // added - Base PSF, Current PSF and Lease Cost were unreachable entirely.
    // Only contextual columns are withheld, since the pages that don't supply
    // their data have nothing to show.
    availableTableColumns() {
      return this.tableColumns.filter(col => {
        if (col.contextual) {
          return col.showInTable !== false;
        }
        return true;
      });
    },
    
    visibleTableColumns() {
      // Filter columns based on visibleColumns array
      return this.tableColumns.filter(col => {
        // Always show columns marked as alwaysVisible
        if (col.alwaysVisible) {
          return true;
        }
        // Show if in visibleColumns array
        return this.visibleColumns.includes(col.field);
      });
    },
    
    filteredSites() {
      let filtered = this.sites;
      
      // Apply archived filter
      if (!this.showArchivedSites) {
        filtered = filtered.filter(site => !site.archived_at);
      }
      
      // Apply lease end date filter
      if (this.leaseEndDateFilter) {
        filtered = filtered.filter(site => {
          if (!site.lease_end_date) {
            return false;
          }
          
          const leaseEndDate = new Date(site.lease_end_date);
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          
          if (this.leaseEndDateFilter === 'expired') {
            return leaseEndDate < now;
          }
          
          if (this.leaseEndDateFilter === 'custom') {
            if (!this.customLeaseStartDate && !this.customLeaseEndDate) {
              return true;
            }
            
            let matchesStart = true;
            let matchesEnd = true;
            
            if (this.customLeaseStartDate) {
              const startDate = new Date(this.customLeaseStartDate);
              startDate.setHours(0, 0, 0, 0);
              matchesStart = leaseEndDate >= startDate;
            }
            
            if (this.customLeaseEndDate) {
              const endDate = new Date(this.customLeaseEndDate);
              endDate.setHours(23, 59, 59, 999);
              matchesEnd = leaseEndDate <= endDate;
            }
            
            return matchesStart && matchesEnd;
          }
          
          const daysFilter = parseInt(this.leaseEndDateFilter);
          const filterDate = new Date(now);
          filterDate.setDate(filterDate.getDate() + daysFilter);
          
          return leaseEndDate >= now && leaseEndDate <= filterDate;
        });
      }
      
      // Apply next key date filter
      if (this.nextKeyDateFilter) {
        filtered = filtered.filter(site => {
          const nextKeyDate = this.getNextKeyDate(site);
          
          if (!nextKeyDate) {
            return false;
          }
          
          const keyDate = new Date(nextKeyDate);
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          
          if (this.nextKeyDateFilter === 'overdue') {
            return keyDate < now;
          }
          
          if (this.nextKeyDateFilter === 'custom') {
            if (!this.customKeyStartDate && !this.customKeyEndDate) {
              return true;
            }
            
            let matchesStart = true;
            let matchesEnd = true;
            
            if (this.customKeyStartDate) {
              const startDate = new Date(this.customKeyStartDate);
              startDate.setHours(0, 0, 0, 0);
              matchesStart = keyDate >= startDate;
            }
            
            if (this.customKeyEndDate) {
              const endDate = new Date(this.customKeyEndDate);
              endDate.setHours(23, 59, 59, 999);
              matchesEnd = keyDate <= endDate;
            }
            
            return matchesStart && matchesEnd;
          }
          
          const daysFilter = parseInt(this.nextKeyDateFilter);
          const filterDate = new Date(now);
          filterDate.setDate(filterDate.getDate() + daysFilter);
          
          return keyDate >= now && keyDate <= filterDate;
        });
      }
      
      // Apply state filter
      if (this.stateFilter) {
        filtered = filtered.filter(site =>
          (site.building?.state || site.state) === this.stateFilter
        );
      }

      // Apply lease/own filter
      if (this.leaseOrOwnFilter) {
        filtered = filtered.filter(site => site.lease_or_own === this.leaseOrOwnFilter);
      }

      // Apply utilization band filter. Bands match the badge thresholds used by
      // getUtilizationBadgeClass so the filter agrees with the colours on screen.
      if (this.utilizationFilter) {
        filtered = filtered.filter(site => {
          const utilization = site.utilization || 0;

          if (this.utilizationFilter === 'over') return utilization > 100;
          if (this.utilizationFilter === '90') return utilization >= 90 && utilization <= 100;
          if (this.utilizationFilter === '70') return utilization >= 70 && utilization < 90;
          if (this.utilizationFilter === 'under70') return utilization < 70;

          return true;
        });
      }

      // Apply size band filter
      if (this.sizeFilter) {
        const [min, max] = this.sizeFilter.split('-');
        const lower = Number(min);
        const upper = max === '' || max === undefined ? Infinity : Number(max);

        filtered = filtered.filter(site => {
          const size = site.size || site.square_footage || 0;
          return size >= lower && size < upper;
        });
      }

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(site => {
          const name = site.building?.address_line_1 || site.name || '';
          const city = site.building?.city || site.city || '';
          const state = site.building?.state || site.state || '';
          const location = `${city} ${state}`.toLowerCase();
          
          return name.toLowerCase().includes(query) || location.includes(query);
        });
      }
      
      // Apply sorting
      if (this.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          let comparison = 0;
          
          // 'waste' is the legacy value for this option; it always sorted on
          // remaining cost, and saved preferences may still hold it.
          if (this.sortBy === 'remaining_lease_cost' || this.sortBy === 'waste') {
            comparison = (b.remaining_lease_cost || 0) - (a.remaining_lease_cost || 0);
          } else if (this.sortBy === 'estimated_waste') {
            comparison = (b.estimated_waste || 0) - (a.estimated_waste || 0);
          } else if (this.sortBy === 'size') {
            comparison = (b.size || b.square_footage || 0) - (a.size || a.square_footage || 0);
          } else if (this.sortBy === 'base_psf') {
            comparison = (b.base_psf || 0) - (a.base_psf || 0);
          } else if (this.sortBy === 'current_psf') {
            comparison = (b.current_psf || 0) - (a.current_psf || 0);
          } else if (this.sortBy === 'time') {
            comparison = (a.due_in_days || 0) - (b.due_in_days || 0);
          } else if (this.sortBy === 'utilization') {
            comparison = (b.utilization || 0) - (a.utilization || 0);
          } else if (this.sortBy === 'address') {
            const addressA = a.building?.address_line_1 || '';
            const addressB = b.building?.address_line_1 || '';
            comparison = addressA.localeCompare(addressB);
          } else if (this.sortBy === 'lease_end_date') {
            const dateA = a.lease_end_date ? new Date(a.lease_end_date) : new Date('9999-12-31');
            const dateB = b.lease_end_date ? new Date(b.lease_end_date) : new Date('9999-12-31');
            comparison = dateA - dateB;
          } else if (this.sortBy === 'next_key_date') {
            const nextKeyDateA = this.getNextKeyDate(a);
            const nextKeyDateB = this.getNextKeyDate(b);
            const dateA = nextKeyDateA ? new Date(nextKeyDateA) : new Date('9999-12-31');
            const dateB = nextKeyDateB ? new Date(nextKeyDateB) : new Date('9999-12-31');
            comparison = dateA - dateB;
          }
          
          return this.sortDirection === 'asc' ? comparison : -comparison;
        });
      }
      
      return filtered.map(site => ({
        ...site,
        image_url: site.image?.view_url || site.building?.image?.view_url || '/img/placeholder-building.jpg'
      }));
    },
    
    totalSites() {
      return this.sites.length;
    },
    
    paginationConfig() {
      if (!this.showPagination) {
        return false;
      }
      
      const totalPages = Math.ceil(this.filteredSites.length / this.displayCount);
      
      return {
        current: this.currentPage,
        total: totalPages,
        perPage: this.displayCount,
        totalItems: this.filteredSites.length
      };
    }
  },
  
  watch: {
    viewMode(newValue) {
      // Save preference to localStorage if enabled
      if (!this.disablePreferences) {
        localStorage.setItem('sites_view_mode', newValue);
      }
      
      // Announce to screen readers
      this.$nextTick(() => {
        const message = newValue === 'grid' ? 'Switched to grid view' : 'Switched to list view';
        this.announceToScreenReader(message);
      });
    },
    
    displayCount(newValue) {
      // Save preference to localStorage if enabled
      if (!this.disablePreferences) {
        localStorage.setItem('sites_display_count', newValue);
      }
      // Reset to first page when changing display count
      this.currentPage = 1;
    },
    
    searchQuery() {
      // Reset to first page when searching
      this.currentPage = 1;
    },
    
    sortBy() {
      // Reset to first page when sorting
      this.currentPage = 1;
    },
    
    leaseEndDateFilter() {
      // Reset to first page when filtering by lease end date
      this.currentPage = 1;
    },
    
    nextKeyDateFilter() {
      // Reset to first page when filtering by next key date
      this.currentPage = 1;
    },

    stateFilter() {
      this.currentPage = 1;
    },

    leaseOrOwnFilter() {
      this.currentPage = 1;
    },

    utilizationFilter() {
      this.currentPage = 1;
    },

    sizeFilter() {
      this.currentPage = 1;
    },
    
    customLeaseStartDate() {
      // Reset to first page when custom lease start date changes
      this.currentPage = 1;
    },
    
    customLeaseEndDate() {
      // Reset to first page when custom lease end date changes
      this.currentPage = 1;
    },
    
    customKeyStartDate() {
      // Reset to first page when custom key start date changes
      this.currentPage = 1;
    },
    
    customKeyEndDate() {
      // Reset to first page when custom key end date changes
      this.currentPage = 1;
    },

    siteData: {
      immediate: true,
      handler(newData) {
        if (newData && newData.length > 0) {
          this.sites = newData;
        }
      }
    },
    
    filteredSites() {
      // Reset to first page when filtered data changes
      this.currentPage = 1;
    }
  },
  
  mounted() {
    // Load saved preferences if enabled
    if (!this.disablePreferences) {
      const savedViewMode = localStorage.getItem('sites_view_mode');
      if (savedViewMode) {
        this.viewMode = savedViewMode;
      }
      
      const savedDisplayCount = localStorage.getItem('sites_display_count');
      if (savedDisplayCount) {
        this.displayCount = parseInt(savedDisplayCount);
      }
      
      // Load saved column visibility preferences
      const savedColumns = localStorage.getItem('sites_table_visible_columns');
      if (savedColumns) {
        try {
          const parsed = JSON.parse(savedColumns);
          // Validate that it's an array
          if (Array.isArray(parsed)) {
            this.visibleColumns = parsed;
          } else {
            console.warn('Invalid column preferences format, resetting to defaults');
            localStorage.removeItem('sites_table_visible_columns');
            this.visibleColumns = this.getDefaultVisibleColumns();
          }
        } catch (e) {
          console.error('Failed to parse saved column preferences:', e);
          // Clear corrupted data
          localStorage.removeItem('sites_table_visible_columns');
          this.visibleColumns = this.getDefaultVisibleColumns();
        }
      } else {
        this.visibleColumns = this.getDefaultVisibleColumns();
      }
    } else {
      this.visibleColumns = this.getDefaultVisibleColumns();
    }
    
    // Load sites data from API if not provided via prop
    if (!this.siteData || this.siteData.length === 0) {
      this.loadSites();
    }
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  
  methods: {
    handlePageChange(page) {
      this.currentPage = page;
      
      // Announce to screen readers
      this.announceToScreenReader(`Page ${page} loaded`);
    },
    
    handleTablePageChange(event) {
      // ThemedTable emits { page } object
      if (event && event.page) {
        this.handlePageChange(event.page);
      }
    },
    
    async loadSites() {
      this.loading = true;
      
      try {
        const response = await window.axios.get('/api/sites');
        this.sites = response.data.data || response.data;
      } catch (error) {
        console.error('Failed to load sites:', error);
        if (this.$toast) {
          this.$toast.error('Failed to load sites');
        }
      } finally {
        this.loading = false;
      }
    },
    
    createSite() {
      window.location.href = '/sites/create';
    },
    
    handleFilterChange({ filter, value }) {
      if (filter === 'showArchivedSites') {
        this.showArchivedSites = value;
      } else if (filter === 'leaseEndDateFilter') {
        this.leaseEndDateFilter = value;
        // Reset custom dates when changing filter type
        if (value !== 'custom') {
          this.customLeaseStartDate = null;
          this.customLeaseEndDate = null;
        }
      } else if (filter === 'nextKeyDateFilter') {
        this.nextKeyDateFilter = value;
        // Reset custom dates when changing filter type
        if (value !== 'custom') {
          this.customKeyStartDate = null;
          this.customKeyEndDate = null;
        }
      } else if (filter === 'customLeaseStartDate') {
        this.customLeaseStartDate = value;
      } else if (filter === 'customLeaseEndDate') {
        this.customLeaseEndDate = value;
      } else if (filter === 'customKeyStartDate') {
        this.customKeyStartDate = value;
      } else if (filter === 'customKeyEndDate') {
        this.customKeyEndDate = value;
      } else if (filter === 'stateFilter') {
        this.stateFilter = value;
      } else if (filter === 'leaseOrOwnFilter') {
        this.leaseOrOwnFilter = value;
      } else if (filter === 'utilizationFilter') {
        this.utilizationFilter = value;
      } else if (filter === 'sizeFilter') {
        this.sizeFilter = value;
      }
    },
    
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    },
    
    // Formatter methods
    formatNumber,
    formatShorthandCurrency,
    formatDate,
    
    // Badge class methods
    getDueInBadgeClass(row) {
      if (row.lease_or_own === 'Owned') {
        return 'badge--green';
      }
      
      if (!row.leases || row.leases.length === 0) {
        return 'badge--yellow';
      }
      
      if (row.due_in_days === 0) {
        return 'badge--red';
      }
      
      const statusColor = row.status_color || '';
      if (statusColor.includes('green') || statusColor.includes('years')) {
        return 'badge--green';
      }
      if (statusColor.includes('orange') || statusColor.includes('months')) {
        return 'badge--yellow';
      }
      if (statusColor.includes('red')) {
        return 'badge--red';
      }
      
      return 'badge--yellow';
    },
    
    // Thresholds must match Site::getUtilizationColorAttribute() and
    // SiteUtilization.getUtilizationColor(). This used to break at >=80/>=50 with
    // no over-100 case, so a site at 83% showed green here and amber on its own
    // detail page.
    getUtilizationBadgeClass(row) {
      const utilization = row.utilization || 0;

      if (utilization > 100) {
        return 'badge--purple';
      }

      if (utilization >= 90) {
        return 'badge--green';
      }

      if (utilization >= 70) {
        return 'badge--yellow';
      }

      return 'badge--red';
    },
    
    handleClickOutside(event) {
      const target = event.target;
      const isDropdown = target.closest('.themed-dropdown');
      
      if (!isDropdown) {
        this.sortFilterOpen = false;
      }
    },
    
    handleAction({ action, row }) {
      if (action === 'view') {
        this.viewSite(row);
      } else if (action === 'edit') {
        this.editSite(row);
      } else if (action === 'delete') {
        this.deleteSite(row);
      }
    },
    
    handleRowClick({ row }) {
      this.viewSite(row);
    },
    
    viewSite(site) {
      window.location.href = `/sites/${site.id}`;
    },
    
    editSite(site) {
      window.location.href = `/sites/${site.id}/edit`;
    },
    
    async deleteSite(site) {
      const siteName = site.building?.address_line_1 || 'this site';
      
      if (!confirm(`Are you sure you want to delete ${siteName}?`)) {
        return;
      }
      
      try {
        await window.axios.delete(`/api/sites/${site.id}`);
        
        // Show success message
        if (this.$toast) {
          this.$toast.success('Site deleted successfully');
        }
        
        // Reload sites
        await this.loadSites();
      } catch (error) {
        console.error('Failed to delete site:', error);
        
        // Show error message
        if (this.$toast) {
          const message = error.response?.data?.message || 'Failed to delete site';
          this.$toast.error(message);
        }
      }
    },
    
    clearFilters() {
      this.sortBy = '';
      this.sortDirection = 'asc';
      this.showArchivedSites = false;
      this.leaseEndDateFilter = '';
      this.nextKeyDateFilter = '';
      this.customLeaseStartDate = null;
      this.customLeaseEndDate = null;
      this.customKeyStartDate = null;
      this.customKeyEndDate = null;
      this.stateFilter = '';
      this.leaseOrOwnFilter = '';
      this.utilizationFilter = '';
      this.sizeFilter = '';
      this.searchQuery = '';
      this.currentPage = 1;
      
      // Announce to screen readers
      this.announceToScreenReader('Filters cleared');
    },
    
    announceToScreenReader(message) {
      // Create a live region for screen reader announcements
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      liveRegion.textContent = message;
      
      document.body.appendChild(liveRegion);
      
      setTimeout(() => {
        document.body.removeChild(liveRegion);
      }, 1000);
    },
    
    handleColumnVisibilityChange({ field, visible }) {
      // Update visible columns array
      if (visible) {
        // Add field if not already in array
        if (!this.visibleColumns.includes(field)) {
          this.visibleColumns.push(field);
        }
      } else {
        // Remove field from array
        const index = this.visibleColumns.indexOf(field);
        if (index > -1) {
          this.visibleColumns.splice(index, 1);
        }
      }
      
      // Save to localStorage if preferences are enabled
      if (!this.disablePreferences) {
        localStorage.setItem('sites_table_visible_columns', JSON.stringify(this.visibleColumns));
      }
      
      // Announce to screen readers
      const columnCount = this.visibleColumns.length;
      this.announceToScreenReader(`${columnCount} columns visible`);
    },
    
    getDefaultVisibleColumns() {
      // Return default visible columns (those with showInTable: true)
      return this.tableColumns
        .filter(col => col.showInTable === true)
        .map(col => col.field);
    },
    
    getNextKeyDate(site) {
      if (!site.key_dates || site.key_dates.length === 0) {
        return null;
      }
      
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      
      const upcomingDates = site.key_dates
        .filter(keyDate => {
          if (keyDate.completed_at) return false;
          const occursOn = new Date(keyDate.occurs_on);
          occursOn.setHours(0, 0, 0, 0);
          return occursOn >= now;
        })
        .sort((a, b) => new Date(a.occurs_on) - new Date(b.occurs_on));
      
      return upcomingDates.length > 0 ? upcomingDates[0].occurs_on : null;
    }
  }
};
</script>
